import { PricingResultType } from '../pages/central/settlement/graphql/mutations/pricingQueryMutations';
import dayjs from 'dayjs';
import * as XLSX from 'xlsx';

/**
 * 导出数据为CSV格式
 * @param data 要导出的数据
 * @param filename 文件名
 */
export const exportToCSV = (data: PricingResultType[], filename: string = 'export') => {
    if (!data || data.length === 0) {
        console.warn('没有数据可导出');
        return;
    }

    // 定义CSV表头
    const headers = [
        '运单号',
        '外部订单号', 
        '客户ID',
        '业务代码',
        '业务场景代码',
        '业务费用代码',
        '计费金额(美分)',
        '状态',
        '定价时间',
        '业务时间',
        '计费失败原因'
    ];

    // 转换数据为CSV格式
    const csvContent = [
        headers.join(','),
        ...data.map(record => [
            record.businessId || '',
            record.otherBusinessId || '',
            record.clientId || '',
            record.businessCode || '',
            record.businessScenarioCode || '',
            record.businessFeeCode || '',
            record.amount || 0,
            record.isSuccess ? '计费成功' : '计费失败',
            dayjs(record.pricingAt).format('YYYY-MM-DD HH:mm:ss'),
            dayjs(record.businessAt).format('YYYY-MM-DD HH:mm:ss'),
            record.billingContentJson ? `"${record.billingContentJson.replace(/"/g, '""')}"` : ''
        ].join(','))
    ].join('\n');

    // 创建并下载文件
    const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `${filename}_${dayjs().format('YYYY-MM-DD_HH-mm-ss')}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
};

/**
 * 导出数据为Excel格式 (使用xlsx库)
 * @param data 要导出的数据
 * @param filename 文件名
 */
export const exportToExcel = (data: PricingResultType[], filename: string = 'export') => {
    if (!data || data.length === 0) {
        console.warn('没有数据可导出');
        return;
    }

    // 准备数据
    const worksheetData = [
        [
            '运单号',
            '外部订单号', 
            '客户ID',
            '业务代码',
            '业务场景代码',
            '业务费用代码',
            '计费金额(美分)',
            '状态',
            '定价时间',
            '业务时间',
            '计费失败原因'
        ],
        ...data.map(record => [
            record.businessId || '',
            record.otherBusinessId || '',
            record.clientId || '',
            record.businessCode || '',
            record.businessScenarioCode || '',
            record.businessFeeCode || '',
            record.amount || 0,
            record.isSuccess ? '计费成功' : '计费失败',
            dayjs(record.pricingAt).format('YYYY-MM-DD HH:mm:ss'),
            dayjs(record.businessAt).format('YYYY-MM-DD HH:mm:ss'),
            record.billingContentJson || ''
        ])
    ];

    // 创建工作簿和工作表
    const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, '计费查询结果');

    // 设置列宽
    const colWidths = [
        { wch: 20 }, // 运单号
        { wch: 20 }, // 外部订单号
        { wch: 10 }, // 客户ID
        { wch: 12 }, // 业务代码
        { wch: 15 }, // 业务场景代码
        { wch: 15 }, // 业务费用代码
        { wch: 15 }, // 计费金额
        { wch: 12 }, // 状态
        { wch: 20 }, // 定价时间
        { wch: 20 }, // 业务时间
        { wch: 30 }  // 计费失败原因
    ];
    worksheet['!cols'] = colWidths;

    // 生成Excel文件并下载
    const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `${filename}_${dayjs().format('YYYY-MM-DD_HH-mm-ss')}.xlsx`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
};

/**
 * 导出数据为JSON格式
 * @param data 要导出的数据
 * @param filename 文件名
 */
export const exportToJSON = (data: PricingResultType[], filename: string = 'export') => {
    if (!data || data.length === 0) {
        console.warn('没有数据可导出');
        return;
    }

    const jsonContent = JSON.stringify(data, null, 2);
    const blob = new Blob([jsonContent], { type: 'application/json;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `${filename}_${dayjs().format('YYYY-MM-DD_HH-mm-ss')}.json`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
};

/**
 * 通用导出函数：导出数据为CSV格式
 * @param data 要导出的数据数组
 * @param headers CSV表头数组
 * @param getRowData 从数据项中提取行数据的函数
 * @param filename 文件名
 */
export const exportGenericToCSV = <T>(
    data: T[],
    headers: string[],
    getRowData: (record: T) => (string | number)[],
    filename: string = 'export'
) => {
    if (!data || data.length === 0) {
        console.warn('没有数据可导出');
        return;
    }

    // 转换数据为CSV格式
    const csvContent = [
        headers.join(','),
        ...data.map(record => {
            const rowData = getRowData(record);
            return rowData.map(cell => {
                // 处理包含逗号、引号或换行符的字段
                // 使用 ?? 而不是 ||，避免将 0、false 等 falsy 值替换为空字符串
                const cellStr = String(cell ?? '');
                if (cellStr.includes(',') || cellStr.includes('"') || cellStr.includes('\n')) {
                    return `"${cellStr.replace(/"/g, '""')}"`;
                }
                return cellStr;
            }).join(',');
        })
    ].join('\n');

    // 创建并下载文件
    const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `${filename}_${dayjs().format('YYYY-MM-DD_HH-mm-ss')}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
};

/**
 * 通用导出函数：导出数据为Excel格式
 * @param data 要导出的数据数组
 * @param headers Excel表头数组
 * @param getRowData 从数据项中提取行数据的函数
 * @param filename 文件名
 * @param sheetName 工作表名称
 * @param colWidths 列宽数组（可选）
 */
export const exportGenericToExcel = <T>(
    data: T[],
    headers: string[],
    getRowData: (record: T) => (string | number)[],
    filename: string = 'export',
    sheetName: string = 'Sheet1',
    colWidths?: { wch: number }[]
) => {
    if (!data || data.length === 0) {
        console.warn('没有数据可导出');
        return;
    }

    // 准备数据
    // 使用 ?? 而不是 ||，避免将 0、false 等 falsy 值替换为空字符串
    const worksheetData = [
        headers,
        ...data.map(record => getRowData(record).map(cell => cell ?? ''))
    ];

    // 创建工作簿和工作表
    const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);

    // 设置列宽
    if (colWidths && colWidths.length > 0) {
        worksheet['!cols'] = colWidths;
    }

    // 生成Excel文件并下载
    const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `${filename}_${dayjs().format('YYYY-MM-DD_HH-mm-ss')}.xlsx`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
};

/**
 * 通用导出函数：导出数据为JSON格式
 * @param data 要导出的数据数组
 * @param filename 文件名
 */
export const exportGenericToJSON = <T>(data: T[], filename: string = 'export') => {
    if (!data || data.length === 0) {
        console.warn('没有数据可导出');
        return;
    }

    const jsonContent = JSON.stringify(data, null, 2);
    const blob = new Blob([jsonContent], { type: 'application/json;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `${filename}_${dayjs().format('YYYY-MM-DD_HH-mm-ss')}.json`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
};